import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { focusFirstError } from "@/lib/formA11y";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import { newIdempotencyKey } from "@/lib/idempotency";
import {
  ApiClientError,
  type Integration,
  type Project,
  type ProjectConnection,
  type ProjectConnectionsPage,
  type TaskKind,
} from "@/lib/types";
import { useSession } from "@/auth/SessionContext";
import { AuthStatePanels } from "@/components/AuthState";
import { Button } from "@/components/Button";
import { ButtonLink } from "@/components/ButtonLink";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { DemoChip } from "@/components/DemoChip";
import { Field, TextArea, TextInput } from "@/components/Field";
import { FolderKanban } from "@/components/icons";
import { LoadList } from "@/components/LoadList";
import { ProjectActivityPanel } from "@/components/ProjectActivity";
import { ProjectAlertsPanel } from "@/components/ProjectAlerts";
import { ProjectServicesPanel } from "@/components/ProjectServices";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";

function connectionRail(verification: string): string {
  if (verification === "verified") return "succeeded";
  if (verification === "failed") return "failed";
  if (verification === "stale") return "stale";
  return "accepted";
}

function setupMark(ready: boolean | null): string {
  if (ready === null) return "…";
  return ready ? "✓" : "—";
}

export function ProjectsPage() {
  const { m, locale } = useLocale();
  const [items, setItems] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.projects();
      setItems(res.items);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e : new ApiClientError(0, "error", m.states.error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next["project-name"] = m.projects.nameRequired;
    setFieldErrors(next);
    if (Object.keys(next).length) {
      queueMicrotask(() => focusFirstError(next, ["project-name"]));
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await api.createProject({ name: name.trim(), description: description.trim() });
      setName("");
      setDescription("");
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : m.projects.createFailed);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadList title={m.common.loading} />;

  return (
    <AuthStatePanels loading={false} error={error}>
      <div>
        <h1 className={shell.pageTitle}>{m.projects.title}</h1>
        <p className={shell.pageLead}>{m.projects.lead}</p>

        <form
          noValidate
          onSubmit={onCreate}
          className={ui.panel}
          style={{ display: "grid", gap: "0.75rem", marginBottom: "1.25rem" }}
        >
          <h2 className="display" style={{ margin: 0, fontSize: "1.1rem" }}>
            {m.projects.newTitle}
          </h2>
          <Field label={m.projects.name} htmlFor="project-name" error={fieldErrors["project-name"]}>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={m.projects.description} htmlFor="project-desc">
            <TextArea
              className="resize-none"
              style={{ resize: "none" }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          {formError ? (
            <p className={ui.fieldError} role="alert">
              {formError}
            </p>
          ) : null}
          <Button type="submit" disabled={busy} aria-busy={busy || undefined}>
            {busy ? m.common.creating : m.common.create}
          </Button>
        </form>

        {items.length === 0 ? (
          <StatePanel kind="empty" title={m.projects.empty} message={m.projects.emptyBody} />
        ) : (
          items.map((project) => (
            <Link key={project.id} to={`/app/projects/${project.id}`} className={ui.row}>
              <StatusRail state="available" />
              <span className={ui.rowMain}>
                <span className={ui.rowIcon}>
                  <FolderKanban size={18} aria-hidden />
                </span>
                <span>
                <strong>{project.name}</strong>
                <div className={ui.meta}>{project.description || m.common.noDescription}</div>
                <div className={ui.meta}>{formatWhen(project.created_at, locale)}</div>
                </span>
              </span>
            </Link>
          ))
        )}
      </div>
    </AuthStatePanels>
  );
}

export function ProjectDetailPage() {
  const { projectId = "" } = useParams();
  const { isOperator } = useSession();
  const { m, locale, fmt } = useLocale();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [kind, setKind] = useState<TaskKind>("plan");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [connections, setConnections] = useState<ProjectConnection[]>([]);
  const [capabilities, setCapabilities] = useState<ProjectConnectionsPage["capabilities"] | null>(
    null,
  );
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);
  const [connectionsRetry, setConnectionsRetry] = useState(0);
  const [available, setAvailable] = useState<Integration[]>([]);
  const [availableError, setAvailableError] = useState<string | null>(null);
  const [availableRetry, setAvailableRetry] = useState(0);
  const [attachId, setAttachId] = useState("");
  const [attachBusy, setAttachBusy] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [detachTarget, setDetachTarget] = useState<ProjectConnection | null>(null);
  const [detachBusy, setDetachBusy] = useState(false);
  const [detachError, setDetachError] = useState<string | null>(null);

  useEffect(() => {
    setProject(null);
    setError(null);
    setLoading(true);
    setConnections([]);
    setCapabilities(null);
    setConnectionsError(null);
    setAttachId("");
    setAttachError(null);
    setDetachTarget(null);
    setDetachError(null);
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    setLoading(true);
    api
      .project(projectId)
      .then((p) => {
        if (!alive) return;
        setProject(p);
        setError(null);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiClientError ? e : new ApiClientError(0, "error", m.states.error));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [projectId, m.states.error]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    setConnectionsLoading(true);
    setConnectionsError(null);
    api
      .projectConnections(projectId, ac.signal)
      .then((page) => {
        if (!alive) return;
        setConnections(page.items);
        setCapabilities(page.capabilities);
        setConnectionsError(null);
      })
      .catch((e) => {
        if (!alive || ac.signal.aborted) return;
        setConnections([]);
        setCapabilities(null);
        setConnectionsError(e instanceof Error ? e.message : m.projects.connectionsFailed);
      })
      .finally(() => {
        if (alive) setConnectionsLoading(false);
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [projectId, connectionsRetry, m.projects.connectionsFailed]);

  useEffect(() => {
    let alive = true;
    setAvailableError(null);
    api
      .integrations()
      .then((res) => {
        if (!alive) return;
        setAvailable(res.items);
        setAvailableError(null);
      })
      .catch((e) => {
        if (!alive) return;
        setAvailable([]);
        setAvailableError(e instanceof Error ? e.message : m.projects.availableFailed);
      });
    return () => {
      alive = false;
    };
  }, [availableRetry, m.projects.availableFailed]);

  const attachedIds = new Set(connections.map((c) => c.id));
  const attachOptions = available.filter((item) => !attachedIds.has(item.id));
  const verificationCounts = connections.reduce(
    (acc, item) => {
      acc[item.verification] = (acc[item.verification] ?? 0) + 1;
      return acc;
    },
    {} as Partial<Record<ProjectConnection["verification"], number>>,
  );
  const hasAttached = connections.length > 0;
  const hasFreshVerified = (verificationCounts.verified ?? 0) > 0;
  const checklistReady = capabilities?.checklist === true;
  const aiUnavailable = capabilities ? capabilities.ai_execution === false : true;
  const repoUnavailable = capabilities ? capabilities.repository_execution === false : true;

  async function onCreateTask(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!title.trim()) next["task-title"] = m.projects.titleRequired;
    if (!instructions.trim()) next["task-ins"] = m.projects.instructionsRequired;
    if (kind === "diagnostics" && !isOperator) {
      next["task-kind"] = m.projects.diagnosticsOnlyOperator;
    }
    setFieldErrors(next);
    if (Object.keys(next).length) {
      queueMicrotask(() => focusFirstError(next, ["task-title", "task-ins", "task-kind"]));
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const op = await api.createTask(
        projectId,
        { title: title.trim(), instructions: instructions.trim(), kind },
        newIdempotencyKey(),
      );
      navigate(`/app/operations/${op.id}`);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : m.projects.createTaskFailed);
    } finally {
      setBusy(false);
    }
  }

  async function onAttach() {
    if (!attachId || attachBusy) return;
    setAttachBusy(true);
    setAttachError(null);
    try {
      const bound = await api.attachProjectConnection(projectId, attachId);
      setConnections((prev) => {
        if (prev.some((c) => c.id === bound.id)) {
          return prev.map((c) => (c.id === bound.id ? bound : c));
        }
        return [...prev, bound];
      });
      setAttachId("");
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : m.projects.attachFailed);
    } finally {
      setAttachBusy(false);
    }
  }

  async function onDetach() {
    if (!detachTarget) return;
    const id = detachTarget.id;
    setDetachBusy(true);
    setDetachError(null);
    try {
      await api.detachProjectConnection(projectId, id);
      setConnections((prev) => prev.filter((c) => c.id !== id));
      setDetachTarget(null);
    } catch (err) {
      setDetachError(err instanceof Error ? err.message : m.projects.detachFailed);
    } finally {
      setDetachBusy(false);
    }
  }

  return (
    <AuthStatePanels
      loading={loading}
      error={error}
      empty={!project ? <StatePanel kind="empty" title={m.states.empty} /> : null}
    >
      {project ? (
        <div>
          <p className={ui.meta}>
            <Link to="/app/projects">← {m.projects.title}</Link>
          </p>
          <h1 className={shell.pageTitle}>{project.name}</h1>
          <p className={shell.pageLead}>{project.description || m.common.noDescription}</p>
          <p className={`mono ${ui.meta}`}>id {project.id}</p>
          <p className={ui.meta}>{m.projects.detailLead}</p>

          <ProjectActivityPanel projectId={projectId} />
          <ProjectServicesPanel key={projectId} projectId={projectId} />
          <ProjectAlertsPanel key={`alerts-${projectId}`} projectId={projectId} />

          <section className={ui.panel} style={{ marginTop: "1.25rem" }} aria-labelledby="project-setup">
            <h2 id="project-setup" className="display" style={{ margin: 0, fontSize: "1.1rem" }}>
              {m.projects.setupTitle}
            </h2>
            <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
              {m.projects.setupLead}
            </p>

            {connectionsLoading ? <StatePanel kind="pending" title={m.common.loading} /> : null}

            {connectionsError ? (
              <div role="alert" style={{ marginTop: "0.75rem" }}>
                <p className={ui.fieldError}>{connectionsError}</p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setConnectionsRetry((n) => n + 1)}
                >
                  {m.projects.retryConnections}
                </Button>
              </div>
            ) : null}

            {!connectionsLoading && !connectionsError ? (
              <ol
                style={{
                  margin: "0.85rem 0 0",
                  paddingLeft: "1.2rem",
                  color: "var(--color-ink-secondary)",
                  display: "grid",
                  gap: "0.55rem",
                }}
              >
                <li>
                  <strong style={{ color: "var(--color-ink)" }}>
                    {setupMark(hasAttached)} {m.projects.setupConnections}
                  </strong>
                  <div className={ui.meta}>
                    {hasAttached
                      ? fmt(m.projects.setupConnectionsReady, { count: connections.length })
                      : m.projects.setupConnectionsNeeded}
                  </div>
                </li>
                <li>
                  <strong style={{ color: "var(--color-ink)" }}>
                    {setupMark(hasAttached ? hasFreshVerified : false)} {m.projects.setupVerification}
                  </strong>
                  <div className={ui.meta}>
                    {!hasAttached
                      ? m.projects.setupVerificationNone
                      : fmt(m.projects.setupVerificationSummary, {
                          verified: verificationCounts.verified ?? 0,
                          stale: verificationCounts.stale ?? 0,
                          failed: verificationCounts.failed ?? 0,
                          not_checked: verificationCounts.not_checked ?? 0,
                        })}
                  </div>
                  <div className={ui.meta}>{m.projects.verificationNote}</div>
                </li>
                <li>
                  <strong style={{ color: "var(--color-ink)" }}>
                    {setupMark(checklistReady)} {m.projects.setupChecklistTask}
                  </strong>
                  <div className={ui.meta}>
                    {checklistReady
                      ? m.projects.setupChecklistReady
                      : m.projects.setupChecklistUnavailable}
                  </div>
                </li>
                <li>
                  <strong style={{ color: "var(--color-ink)" }}>
                    {setupMark(!aiUnavailable)} {m.projects.setupAi}
                  </strong>
                  <div className={ui.meta}>
                    {aiUnavailable ? m.projects.setupAiUnavailable : m.projects.capAvailable}
                  </div>
                </li>
                <li>
                  <strong style={{ color: "var(--color-ink)" }}>
                    {setupMark(!repoUnavailable)} {m.projects.setupRepo}
                  </strong>
                  <div className={ui.meta}>
                    {repoUnavailable ? m.projects.setupRepoUnavailable : m.projects.capAvailable}
                  </div>
                </li>
                <li>
                  <strong style={{ color: "var(--color-ink)" }}>
                    {setupMark(false)} {m.projects.setupMac}
                  </strong>
                  <div className={ui.meta}>{m.projects.setupMacUnavailable}</div>
                  <div className={ui.meta} style={{ display: "flex", gap: "0.45rem", alignItems: "center", flexWrap: "wrap" }}>
                    <DemoChip />
                    <span>{m.projects.setupDemoHint}</span>
                  </div>
                </li>
              </ol>
            ) : null}

            <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "1rem", flexWrap: "wrap" }}>
              <ButtonLink to={`/app/projects/${projectId}/workspace-requests`} variant="primary">
                {m.projects.workspaceRequestLink}
              </ButtonLink>
              <ButtonLink to="/app/integrations" variant="secondary">
                {m.projects.manageConnections}
              </ButtonLink>
              <ButtonLink to="/app/machines" variant="secondary">
                {m.projects.setupDemoLink}
              </ButtonLink>
              {checklistReady ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    document.getElementById("task-title")?.focus();
                    const reduce =
                      typeof window !== "undefined" &&
                      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
                    document.getElementById("project-new-task")?.scrollIntoView({
                      behavior: reduce ? "auto" : "smooth",
                      block: "start",
                    });
                  }}
                >
                  {m.projects.setupCreateTask}
                </Button>
              ) : null}
            </div>
            <p className={ui.meta} style={{ marginTop: "0.75rem" }}>
              {m.projects.setupNextSteps}
            </p>
          </section>

          <section className={ui.panel} style={{ marginTop: "1.25rem" }}>
            <h2 className="display" style={{ margin: 0, fontSize: "1.1rem" }}>
              {m.projects.connectionsTitle}
            </h2>
            <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
              {m.projects.connectionsLead}
            </p>
            <p className={ui.meta} style={{ marginTop: "0.65rem" }}>
              {m.projects.verificationNote}
            </p>

            {connectionsLoading ? (
              <StatePanel kind="pending" title={m.common.loading} />
            ) : null}

            {connectionsError ? (
              <div role="alert" style={{ marginTop: "0.75rem" }}>
                <p className={ui.fieldError}>{connectionsError}</p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setConnectionsRetry((n) => n + 1)}
                >
                  {m.projects.retryConnections}
                </Button>
              </div>
            ) : null}

            {!connectionsLoading && !connectionsError && connections.length === 0 ? (
              <StatePanel
                kind="empty"
                title={m.projects.connectionsEmpty}
                message={m.projects.connectionsEmptyBody}
              />
            ) : null}

            {!connectionsLoading && !connectionsError
              ? connections.map((item) => (
                  <div key={item.id} className={ui.row} style={{ cursor: "default" }}>
                    <StatusRail state={connectionRail(item.verification)} />
                    <div style={{ width: "100%" }}>
                      <strong>{item.name}</strong>
                      <div className={ui.meta}>
                        {lifecycleLabel(m, item.provider)} · {lifecycleLabel(m, item.status)} ·{" "}
                        {lifecycleLabel(m, item.verification)}
                        {item.last_checked_at
                          ? ` · ${m.integrations.lastChecked} ${formatWhen(item.last_checked_at, locale)}`
                          : ""}
                      </div>
                      <div
                        className={ui.actions}
                        style={{ justifyContent: "flex-start", marginTop: "0.55rem" }}
                      >
                        <Button
                          type="button"
                          variant="danger"
                          disabled={detachBusy}
                          onClick={() => {
                            setDetachError(null);
                            setDetachTarget(item);
                          }}
                        >
                          {m.projects.detach}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              : null}

            <div style={{ display: "grid", gap: "0.75rem", marginTop: "1rem" }}>
              <h3 className="display" style={{ margin: 0, fontSize: "1rem" }}>
                {m.projects.attachTitle}
              </h3>
              {availableError ? (
                <div role="alert">
                  <p className={ui.fieldError}>{availableError}</p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setAvailableRetry((n) => n + 1)}
                  >
                    {m.projects.retryAvailable}
                  </Button>
                </div>
              ) : (
                <>
                  <Field label={m.projects.attachSelect} htmlFor="project-attach">
                    <select
                      id="project-attach"
                      className={ui.input}
                      value={attachId}
                      onChange={(e) => setAttachId(e.target.value)}
                      disabled={attachBusy || attachOptions.length === 0}
                    >
                      <option value="">
                        {attachOptions.length === 0
                          ? m.projects.attachNone
                          : m.projects.attachPlaceholder}
                      </option>
                      {attachOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} · {lifecycleLabel(m, item.provider)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {attachOptions.length === 0 && !availableError ? (
                    <p className={ui.meta}>
                      {available.length === 0
                        ? m.projects.attachNeedConnections
                        : m.projects.attachAllBound}
                    </p>
                  ) : null}
                  <div className={ui.actions} style={{ justifyContent: "flex-start" }}>
                    <Button
                      type="button"
                      disabled={!attachId || attachBusy}
                      aria-busy={attachBusy || undefined}
                      onClick={() => void onAttach()}
                    >
                      {attachBusy ? m.projects.attaching : m.projects.attach}
                    </Button>
                    <ButtonLink to="/app/integrations" variant="secondary">
                      {m.projects.manageConnections}
                    </ButtonLink>
                  </div>
                </>
              )}
              {attachError ? (
                <p className={ui.fieldError} role="alert">
                  {attachError}
                </p>
              ) : null}
            </div>
          </section>

          <form
            id="project-new-task"
            noValidate
            onSubmit={onCreateTask}
            className={ui.panel}
            style={{ display: "grid", gap: "0.75rem", marginTop: "1.25rem" }}
          >
            <h2 className="display" style={{ margin: 0, fontSize: "1.1rem" }}>
              {m.projects.newTask}
            </h2>
            <Field label={m.projects.taskTitle} htmlFor="task-title" error={fieldErrors["task-title"]}>
              <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field
              label={m.projects.instructions}
              htmlFor="task-ins"
              error={fieldErrors["task-ins"]}
            >
              <TextArea
                className="resize-none"
                style={{ resize: "none" }}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
              />
            </Field>
            <Field label={m.projects.kind} htmlFor="task-kind" error={fieldErrors["task-kind"]}>
              <select
                className={ui.input}
                value={kind}
                onChange={(e) => setKind(e.target.value as TaskKind)}
              >
                <option value="plan">{m.projects.kindPlan}</option>
                {isOperator ? <option value="diagnostics">{m.projects.kindDiagnostics}</option> : null}
              </select>
            </Field>
            {formError ? (
              <p className={ui.fieldError} role="alert">
                {formError}
              </p>
            ) : null}
            <Button type="submit" disabled={busy} aria-busy={busy || undefined}>
              {busy ? m.common.sending : m.projects.createTask}
            </Button>
          </form>

          <ConfirmSheet
            open={Boolean(detachTarget)}
            title={m.projects.detachTitle}
            confirmLabel={m.projects.detachConfirm}
            danger
            busy={detachBusy}
            onClose={() => {
              if (detachBusy) return;
              setDetachTarget(null);
              setDetachError(null);
            }}
            onConfirm={() => void onDetach()}
          >
            <p>{m.projects.detachBody}</p>
            {detachTarget ? (
              <p className={ui.meta}>
                {detachTarget.name} · {lifecycleLabel(m, detachTarget.provider)}
              </p>
            ) : null}
            {detachError ? (
              <p className={ui.fieldError} role="alert">
                {detachError}
              </p>
            ) : null}
          </ConfirmSheet>
        </div>
      ) : null}
    </AuthStatePanels>
  );
}
